import { TCAppConfig } from "../../../../shared/app-configs/tc-app-configs";
import apiService from "../../../../shared/service/api-service";

// ─── Session-scoped reference-data cache ─────────────────────────────────────
// Stores in-flight Promises by key so that:
//   • Concurrent callers awaiting the same key share ONE network request.
//   • Resolved responses are returned instantly on subsequent calls.
//   • Failed requests are evicted so the next call retries cleanly.
// Call clearServiceCache() on user logout to prevent stale data in new sessions.
const _cache = new Map<string, Promise<any>>();

function cached(key: string, fetcher: () => Promise<any>): Promise<any> {
    if (!_cache.has(key)) {
        _cache.set(
            key,
            fetcher().catch((err) => {
                _cache.delete(key); // allow retry on failure
                throw err;
            })
        );
    }
    return _cache.get(key)!;
}

/** Call this on logout / SC change to purge session-cached reference data. */
export function clearServiceCache(): void {
    _cache.clear();
}
// ─────────────────────────────────────────────────────────────────────────────

export const dtrackItServiceV4 = {
    // Static reference data — safe to cache for the session
    getOrganicFarmStandards() {
        return cached("organicFarmStandards", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_ORGANIC_FARM_CODE,
            })
        );
    },

    getStandradLabelGrade() {
        return cached("standardLabelGrade", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_STANDARD_LABEL_GRADE,
            })
        );
    },

    getProductCategory() {
        return cached("productCategory", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_PRODUCT_CODE,
            })
        );
    },

    getProductDetailsCode() {
        return cached("productDetailsCode", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_PRODUCT_DETAIL_CODE,
            })
        );
    },

    getCountry() {
        return cached("countries", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_COUNTRY_CODE,
            })
        );
    },

    // Keyed per country so each country's states are cached independently
    getStateBasedonCountry(country: any) {
        return cached(`states:${country}`, () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_STATE_CODE,
                country,
            })
        );
    },

    getRawMaterialCodes() {
        return cached("rawMaterialCodes", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_RAW_MATERIAL_CODE,
            })
        );
    },

    getCBLicensingCodes() {
        return cached("cbLicensingCodes", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_CBLICENSING_CODE,
            })
        );
    },

    getFacilityProcessCategoryCodes() {
        return cached("facilityProcessCategoryCodes", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_FACILITY_PROCESS_CATEGORY_CODE,
            })
        );
    },

    getSCStatus() {
        return cached("scStatus", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_SC_STATUS,
            })
        );
    },

    getSCStatusV4() {
        return cached("scStatusV4", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_SC_STATUS_V4,
            })
        );
    },

    getSuspensionReason() {
        return cached("suspensionReason", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_SUSPENSION_REASON,
            })
        );
    },

    getScStandardValues() {
        return cached("scStandardValues", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_SC_STANDARD_VALUES,
            })
        );
    },

    getStandardWithdrawalReasonValues() {
        return cached("standardWithdrawalReasonValues", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_STANDARD_WITHDRAWAL_REASON_VALUES,
            })
        );
    },

    getCertifiedOrganizationApprovedClaimsValues() {
        return cached("coApprovedClaimsValues", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_CERTIFIED_ORGANIZATION_APPROVED_CLAIMS_VALUES,
            })
        );
    },

    getCertifiedOrganizationApprovedClaimsValuesLG() {
        return cached("coApprovedClaimsValuesLG", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_STANDARD_LABEL_GRADE,
            })
        );
    },

    getAccreditationBody() {
        return cached("accreditationBody", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_ACCREDITATION_BODY,
            })
        );
    },

    getScNonMandatoryCriterionStandardValues() {
        return cached("nmcStandardValues", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_SC_NON_MANDATORY_CRITERION_STANDARD_VALUES,
            })
        );
    },

    getFacilityType() {
        return cached("facilityType", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_FACILITY_TYPE,
            })
        );
    },

    getFacilityCbOfIndependentlyCertifiedSubcontractor() {
        return cached("facilityCbIndependentSubcontractor", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: TCAppConfig.GET_FACILITY_CB_OF_INDEPENDENTLY_CERTIFIED_SUBCONTRACTOR,
            })
        );
    },

    // Keyed per standard so each standard's versions are cached independently
    getStandardVersions(standard: string) {
        return cached(`standardVersions:${standard}`, () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: "VER",
                standard,
            })
        );
    },

    getNonMandatoryCriterionStandardValues() {
        return cached("nmcStandardVals", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: "NMCSD",
            })
        );
    },

    // Keyed per standard
    getNonMandatoryCriterionStandardVersions(standard: string) {
        return cached(`nmcStandardVersions:${standard}`, () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: "NMCVER",
                standard,
            })
        );
    },

    getNonMandatoryCriterionNumbers() {
        return cached("nmcNumbers", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: "NMCRI",
            })
        );
    },

    getNonMandatoryCriterionStatusValues() {
        return cached("nmcStatusValues", () =>
            apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
                query_param: "NMCSTS",
            })
        );
    },

    // ── Context-dependent / non-idempotent — intentionally NOT cached ─────────

    /** Standard-label-grade lookup filtered by user's currently selected standards. */
    getStandradLabelGradeOnBasisOfSelectedStandard(standard: string[]) {
        return apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, {
            standard: [...standard, "NA"],
        });
    },

    getDTrackItSCNumbers(scCbLicensingCode: string) {
        return apiService.get<any>(TCAppConfig.GET_DTRACKIT_SC_NO_V4, { scCbLicensingCode });
    },

    getDTrackItCONames(scCbLicensingCode: string) {
        return apiService.get<any>(TCAppConfig.GET_DTRACKIT_CO_NAME_V4, { scCbLicensingCode });
    },

    getSCDTrackItTransactions(scCbLicensingCode: string) {
        return apiService.get<any>(TCAppConfig.GET_SC_DTRACKIT_TRANSACTIONS_V4, { scCbLicensingCode });
    },

    /** Generic ref-code lookup — caller controls params, NOT safe to cache globally. */
    getDTrackItRefCodes(queryParam: string, country?: string, limit?: number, offset?: number, standard?: string) {
        const queryParams: any = { query_param: queryParam };
        if (country)   queryParams.country   = country;
        if (limit)     queryParams.limit     = limit;
        if (offset)    queryParams.offset    = offset;
        if (standard)  queryParams.standard  = standard;
        return apiService.get<any>(TCAppConfig.GET_DTRACKIT_REF_CODE_V4, queryParams);
    },
};
